/**
 * TournamentsPage.js — List and create tournaments
 * CSS prefix: tp-
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import * as api from '../services/api';
import './TournamentsPage.css';

const SPORT_OPTIONS = [
  'general', 'soccer', 'basketball', 'volleyball', 'softball',
  'hockey', 'football', 'pool', 'darts'
];

const FORMAT_OPTIONS = [
  { value: 'single_elim', label: 'Single Elimination' },
  { value: 'double_elim', label: 'Double Elimination' },
  { value: 'round_robin', label: 'Round Robin' },
  { value: 'group_knockout', label: 'Group Stage → Knockout' },
];

const STATUS_LABELS = {
  draft: 'Draft',
  registration_open: 'Registration Open',
  registration_closed: 'Reg. Closed',
  in_progress: 'In Progress',
  completed: 'Completed',
};

const STATUS_CLASSES = {
  draft: 'tp-status-draft',
  registration_open: 'tp-status-open',
  registration_closed: 'tp-status-closed',
  in_progress: 'tp-status-active',
  completed: 'tp-status-done',
};

const FORMAT_LABELS = {
  single_elim: 'Single Elim',
  double_elim: 'Double Elim',
  round_robin: 'Round Robin',
  group_knockout: 'Group/Knockout',
  swiss: 'Swiss',
};

function TournamentsPage({ onBack, onManage }) {
  const { colors } = useTheme();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    sport: 'general',
    formatType: 'single_elim',
    startDate: '',
    endDate: '',
    registrationDeadline: '',
    maxTeams: '',
    minTeams: '',
    entryFee: '',
    description: '',
  });

  const [confirmDelete, setConfirmDelete] = useState(null); // { id, name }
  const [deleting, setDeleting] = useState(null);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.getTournaments();
      setTournaments(res.tournaments || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.name.trim()) return showToast('Name is required');
    if (!createForm.startDate) return showToast('Start date is required');
    try {
      setCreating(true);
      const payload = {
        name: createForm.name.trim(),
        sport: createForm.sport,
        formatType: createForm.formatType,
        startDate: createForm.startDate,
        endDate: createForm.endDate || undefined,
        registrationDeadline: createForm.registrationDeadline || undefined,
        maxTeams: createForm.maxTeams ? parseInt(createForm.maxTeams, 10) : undefined,
        minTeams: createForm.minTeams ? parseInt(createForm.minTeams, 10) : undefined,
        entryFee: createForm.entryFee ? parseFloat(createForm.entryFee) : undefined,
        description: createForm.description || undefined,
      };
      const res = await api.createTournament(payload);
      setShowCreate(false);
      setCreateForm({ name: '', sport: 'general', formatType: 'single_elim', startDate: '', endDate: '', registrationDeadline: '', maxTeams: '', minTeams: '', entryFee: '', description: '' });
      showToast('Tournament created!', 'success');
      await load();
      if (onManage && res.tournament) onManage(res.tournament.id);
    } catch (err) {
      showToast(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      setDeleting(confirmDelete.id);
      await api.deleteTournament(confirmDelete.id);
      setConfirmDelete(null);
      showToast('Tournament deleted', 'success');
      await load();
    } catch (err) {
      showToast(err.message);
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="tp-page" style={{ background: colors.bg, color: colors.text }}>
      {toast && (
        <div className={`tp-toast tp-toast-${toast.type}`}>{toast.msg}</div>
      )}

      <div className="tp-header">
        <button className="tp-back-btn" onClick={onBack}>← Back</button>
        <h1 className="tp-title">Tournaments</h1>
        <button className="tp-create-btn" onClick={() => setShowCreate(true)}>+ New Tournament</button>
      </div>

      {loading ? (
        <div className="tp-loading">Loading tournaments…</div>
      ) : error ? (
        <div className="tp-error">{error}</div>
      ) : tournaments.length === 0 ? (
        <div className="tp-empty">
          <p>No tournaments yet.</p>
          <button className="tp-create-btn" onClick={() => setShowCreate(true)}>Create your first tournament</button>
        </div>
      ) : (
        <div className="tp-list">
          {tournaments.map(t => (
            <div
              key={t.id}
              className="tp-card"
              style={{ background: colors.card, borderColor: colors.border }}
            >
              <div className="tp-card-main">
                <div className="tp-card-badges">
                  <span className={`tp-status-badge ${STATUS_CLASSES[t.status] || ''}`}>
                    {STATUS_LABELS[t.status] || t.status}
                  </span>
                  <span className="tp-format-badge">{FORMAT_LABELS[t.formatType] || t.formatType}</span>
                  <span className="tp-sport-badge">{t.sport}</span>
                </div>
                <h2 className="tp-card-name">{t.name}</h2>
                <div className="tp-card-meta">
                  <span>{formatDate(t.startDate)}{t.endDate && t.endDate !== t.startDate ? ` – ${formatDate(t.endDate)}` : ''}</span>
                  <span className="tp-meta-sep">·</span>
                  <span>{t.teamCount ?? 0} teams</span>
                  {t.maxTeams && <><span className="tp-meta-sep">·</span><span>max {t.maxTeams}</span></>}
                </div>
              </div>
              <div className="tp-card-actions">
                <button className="tp-manage-btn" onClick={() => onManage(t.id)}>Manage</button>
                <button
                  className="tp-delete-btn"
                  onClick={() => setConfirmDelete({ id: t.id, name: t.name })}
                >Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="tp-modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="tp-modal" onClick={e => e.stopPropagation()} style={{ background: colors.card }}>
            <h2 className="tp-modal-title">New Tournament</h2>
            <form onSubmit={handleCreate} className="tp-create-form">
              <div className="tp-form-row">
                <label>Name *</label>
                <input
                  value={createForm.name}
                  onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Summer Kickball Tournament"
                  required
                />
              </div>
              <div className="tp-form-row tp-form-row-2">
                <div>
                  <label>Sport</label>
                  <select value={createForm.sport} onChange={e => setCreateForm(f => ({ ...f, sport: e.target.value }))}>
                    {SPORT_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label>Format</label>
                  <select value={createForm.formatType} onChange={e => setCreateForm(f => ({ ...f, formatType: e.target.value }))}>
                    {FORMAT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="tp-form-row tp-form-row-2">
                <div>
                  <label>Start Date *</label>
                  <input type="date" value={createForm.startDate} onChange={e => setCreateForm(f => ({ ...f, startDate: e.target.value }))} required />
                </div>
                <div>
                  <label>End Date</label>
                  <input type="date" value={createForm.endDate} onChange={e => setCreateForm(f => ({ ...f, endDate: e.target.value }))} />
                </div>
              </div>
              <div className="tp-form-row tp-form-row-2">
                <div>
                  <label>Registration Deadline</label>
                  <input type="date" value={createForm.registrationDeadline} onChange={e => setCreateForm(f => ({ ...f, registrationDeadline: e.target.value }))} />
                </div>
                <div>
                  <label>Entry Fee ($)</label>
                  <input type="number" min="0" step="0.01" value={createForm.entryFee} onChange={e => setCreateForm(f => ({ ...f, entryFee: e.target.value }))} placeholder="0.00" />
                </div>
              </div>
              <div className="tp-form-row tp-form-row-2">
                <div>
                  <label>Min Teams</label>
                  <input type="number" min="2" value={createForm.minTeams} onChange={e => setCreateForm(f => ({ ...f, minTeams: e.target.value }))} placeholder="2" />
                </div>
                <div>
                  <label>Max Teams</label>
                  <input type="number" min="2" value={createForm.maxTeams} onChange={e => setCreateForm(f => ({ ...f, maxTeams: e.target.value }))} placeholder="16" />
                </div>
              </div>
              <div className="tp-form-row">
                <label>Description</label>
                <textarea
                  rows={3}
                  value={createForm.description}
                  onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Optional description…"
                />
              </div>
              <div className="tp-modal-actions">
                <button type="button" className="tp-cancel-btn" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="tp-submit-btn" disabled={creating}>
                  {creating ? 'Creating…' : 'Create Tournament'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {confirmDelete && (
        <div className="tp-modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="tp-modal tp-modal-sm" onClick={e => e.stopPropagation()} style={{ background: colors.card }}>
            <h2 className="tp-modal-title">Delete Tournament?</h2>
            <p className="tp-modal-body">
              Delete <strong>{confirmDelete.name}</strong>? This will remove all teams and match data permanently.
            </p>
            <div className="tp-modal-actions">
              <button className="tp-cancel-btn" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="tp-danger-btn" onClick={handleDelete} disabled={!!deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TournamentsPage;
